// The look: which of the four themes, and light or dark.
//
// A THEME IS A SET OF CUSTOM PROPERTIES and nothing else. Applying one writes
// two attributes on <html> and the properties cascade from `styles.css`; adding
// a fifth theme is a data change in three places and no component touched -
// `web/src/README.md` names all three and says what forgetting each one costs.
// `THEMES` below is one of them, and the only one any module reads.
//
// TWO ATTRIBUTES, not one. `data-theme` is the FAMILY - paper, slate, sage, ink
// - and `data-mode` is the RESOLVED light or dark. They are separate because
// four themes times two modes is eight token sets, and keying eight blocks off
// one attribute would spell every name twice ("paper-dark") in the stylesheet,
// in the storage and here.
//
// `data-mode` IS ALWAYS RESOLVED, never the word "system". The stored choice may
// be `system`, but what reaches the document is `light` or `dark`, because this
// module has to resolve the mode anyway to write the `color-scheme` meta - which
// CSS cannot write and which the browser needs in order to paint its OWN
// surfaces (scroll bars, form controls, the canvas behind the page) in the same
// mode as the page. A `prefers-color-scheme` block in the stylesheet would be a
// SECOND answer to a question already answered here, and two resolvers of one
// question are how the meta and the page come to disagree. So there is one
// resolver, it is this one, and `startAppearance` keeps it live when the
// operating system changes its mind mid-session.

import { useSyncExternalStore } from 'react';
import { readChoice, writePreference } from './preference.ts';

/** The four themes of `prototype-v1.md` §4, and the only values `data-theme` takes. */
export type ThemeName = 'paper' | 'slate' | 'sage' | 'ink';

/** What a person picked. `system` is the default and means "ask the device". */
export type ModeChoice = 'system' | 'light' | 'dark';

/** What actually reaches `data-mode`, once `system` has been asked. */
export type Mode = 'light' | 'dark';

export interface Appearance {
  theme: ThemeName;
  mode: ModeChoice;
}

/**
 * The four, in the prototype's order, with the prototype's own words for each.
 *
 * The note is the design doc's "Character" column shortened to fit beside the
 * name in a card (`prototype-v1.md` §4) - it is what tells a stranger why they
 * would pick Sage over Slate, and a picker of four unlabelled swatches does not.
 */
export const THEMES: readonly { name: ThemeName; label: string; note: string }[] = [
  { name: 'paper', label: 'Paper', note: 'Warm, default' },
  { name: 'slate', label: 'Slate', note: 'Cool, crisp' },
  { name: 'sage', label: 'Sage', note: 'Quiet, low glare' },
  { name: 'ink', label: 'Ink', note: 'Maximum contrast' },
];

/**
 * The mode segment.
 *
 * THE PROTOTYPE OFFERS TWO, Light and Dark, and this offers three. Its `S.mode`
 * was always concrete, so it had nowhere to put "follow the device" - but the
 * requirement is that a person who has chosen nothing gets their device's dark
 * preference, and with only two buttons the first press ever made would end that
 * for good. `System` is the third button so the default state is one a person
 * can also return to, rather than a state that exists only until it is touched.
 */
export const MODES: readonly { value: ModeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** Paper stays the default, and the default mode is the device's own. */
export const DEFAULT_APPEARANCE: Appearance = { theme: 'paper', mode: 'system' };

/**
 * The storage keys, which `index.html` spells out again in plain ES5.
 *
 * IT HAS TO. The pre-paint script runs before this module is fetched, let alone
 * parsed - that is the whole point of it - so it cannot import these. The two
 * copies are kept honest by `test/theme.test.ts`, which runs the real
 * script out of the real `index.html` and asserts it lands on the same
 * attributes this module would. Change one side and that test goes red.
 *
 * That holds on the FAILURE paths as well as the happy one - storage raising,
 * the cookie jar raising, both raising at once - because those are cases in the
 * same matrix and are compared the same way. They are also where the two copies
 * had in fact drifted, which is why the matrix reaches them by name.
 */
export const THEME_KEY = 'pn_theme';
export const MODE_KEY = 'pn_mode';

export function isThemeName(value: unknown): value is ThemeName {
  return THEMES.some((theme) => theme.name === value);
}

export function isModeChoice(value: unknown): value is ModeChoice {
  return MODES.some((mode) => mode.value === value);
}

/**
 * What the device is asking for right now.
 *
 * `matchMedia` is guarded because this is also called from the test environment
 * and from any browser old enough not to have it; absent, the answer is light,
 * which is the same answer the pre-paint script's own fallback gives.
 */
export function systemMode(): Mode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveMode(choice: ModeChoice): Mode {
  return choice === 'system' ? systemMode() : choice;
}

/**
 * WHAT THIS PAGE WAS TOLD TO WEAR, kept beside the two mirrors rather than
 * instead of them.
 *
 * A browser can refuse both `localStorage` and the cookie - private browsing
 * with cookies off, a full quota - and every write in `preference.ts` is
 * swallowed on purpose, because a cosmetic preference may not cost a blank
 * page. Without this, the only record of a choice made in such a browser is the
 * document itself, and `readAppearance` answers the DEFAULT for somebody who has
 * just picked Ink and Dark and is looking at it: the device flips at sunset,
 * `watchSystemMode` re-reads the choice, finds `system`, and overrules them with
 * no way back but pressing the same buttons again.
 *
 * Failing to write a choice down is a reason it will not survive a RELOAD. It is
 * not a reason to overrule it while the page is still up. So it is remembered
 * here whether or not either mirror accepted it, and it is lost on reload -
 * which is the whole of what can be promised when nothing can be stored.
 */
let chosen: Appearance | null = null;

/**
 * What is stored, falling back to the default for anything unrecognised.
 *
 * A VALUE IS VALIDATED RATHER THAN TRUSTED. Storage outlives the code that wrote
 * it: a theme removed in a later version, or a key another tab on the same
 * origin happened to use, would otherwise reach `data-theme` and match no block
 * in the stylesheet at all. What paints then is the bare `:root` fallback, which
 * is Paper - so the app would look right and the picker would show nothing
 * selected, which is the confusing half of a bug without the visible half.
 *
 * The order the three answers are tried in is `readChoice` in `preference.ts`,
 * which owns it for every per-browser preference; this file supplies only its
 * own vocabulary and its own default.
 */
export function readAppearance(): Appearance {
  return { theme: readTheme(), mode: readMode() };
}

function readTheme(): ThemeName {
  return readChoice(THEME_KEY, isThemeName, chosen?.theme ?? null, DEFAULT_APPEARANCE.theme);
}

function readMode(): ModeChoice {
  return readChoice(MODE_KEY, isModeChoice, chosen?.mode ?? null, DEFAULT_APPEARANCE.mode);
}

export function storeAppearance(appearance: Appearance): void {
  chosen = appearance;
  writePreference(THEME_KEY, appearance.theme);
  writePreference(MODE_KEY, appearance.mode);
}

/**
 * Put a look on the document: the two attributes, and the `color-scheme` meta.
 *
 * THE META IS NOT DECORATION AND NOT REDUNDANT WITH THE CSS. `styles.css` also
 * sets `color-scheme` from `data-mode`, and that is the authoritative one once
 * the stylesheet has arrived - but the stylesheet is a separate request, and
 * between the first paint and its arrival the only thing telling the browser
 * which mode to paint its own canvas in is this meta. Leaving it pinned to
 * `light` is the white flash on iOS that `web-architecture.md` §9.3 describes.
 *
 * Returns the mode it resolved to, which is what the picker draws its previews
 * in - so the caller never has to ask the same question a second time.
 */
export function applyAppearance(appearance: Appearance): Mode {
  const mode = resolveMode(appearance.mode);
  const root = document.documentElement;
  root.setAttribute('data-theme', appearance.theme);
  root.setAttribute('data-mode', mode);

  let meta = document.querySelector('meta[name="color-scheme"]');
  if (meta === null) {
    // `index.html` ships one, so this is for the test environment and for any
    // future host page that forgets. Cheaper than a branch at every call site.
    meta = document.createElement('meta');
    meta.setAttribute('name', 'color-scheme');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', mode);

  for (const tell of listeners) tell();
  return mode;
}

// ---------------------------------------------------------- the mode, in React

/*
 * A SECOND READER OF THE MODE, and it is not the stylesheet.
 *
 * Everything a theme changes is a custom property, with one exception the
 * cascade cannot reach: a SCHEDULE'S HUE arrives from the API as a stored hex
 * and is written inline as `--c`, and an inline custom property on an element
 * is not something a stylesheet rule on that element can override. So the
 * mode-appropriate value has to be chosen in the component, which means
 * components need to know the mode - see `hueIn` in `edit/edit-model.ts` for
 * what they do with it and why it is a lookup rather than a calculation.
 *
 * THE DOCUMENT IS THE SNAPSHOT, not a variable beside it. `data-mode` is what
 * CSS reads, so reading the same attribute is what keeps the hue a component
 * paints and the surface it is painted on from ever describing different modes.
 * The subscription exists only to say WHEN to look again; `applyAppearance` is
 * the one thing that moves the attribute, so it is the one thing that tells.
 *
 * No context and no provider, deliberately: a provider would put the look in
 * `App.tsx`, and every screen that draws a hue would then depend on being
 * rendered inside it - including the sheets, which several screens open.
 */
const listeners = new Set<() => void>();

function subscribeMode(tell: () => void): () => void {
  listeners.add(tell);
  return () => {
    listeners.delete(tell);
  };
}

/** The mode the document is actually in, which is the mode CSS is painting. */
export function currentMode(): Mode {
  return document.documentElement.getAttribute('data-mode') === 'dark' ? 'dark' : 'light';
}

/**
 * The mode, for a component that has to pick a colour rather than name a token.
 *
 * `useSyncExternalStore` rather than `useState` and an effect: the value lives
 * on the document, outside React, and this is the hook for exactly that. The
 * server snapshot is the same function because there is no server - the client
 * is static files (§4.3), so there is no render that has no document.
 */
export function useMode(): Mode {
  return useSyncExternalStore(subscribeMode, currentMode, currentMode);
}

/**
 * Be told when the DEVICE changes its appearance, while `system` is the choice.
 *
 * Only `system` listens. A person who chose Dark means Dark, and must not be
 * flipped to light because their laptop reached sunrise - so the choice is
 * re-read on each event rather than closed over, and a listener can never act
 * on a preference that has since been overridden.
 *
 * Returns the way to stop. `matchMedia` is guarded for the test environment,
 * where it may be absent; nothing then ever changes, which is correct.
 */
export function watchSystemMode(onChange: (mode: Mode) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const react = () => {
    if (readAppearance().mode !== 'system') return;
    onChange(systemMode());
  };
  query.addEventListener('change', react);
  return () => query.removeEventListener('change', react);
}

/**
 * Apply the stored look and follow the device, for as long as the app is up.
 *
 * WITHOUT THE FOLLOWING, "System" MEANS "the system, as it was when the page
 * loaded". macOS and iOS switch appearance at sunset on their own, and a
 * planner is exactly the kind of tab that is open across it. Started once from
 * `main.tsx` rather than from a screen, because the screen that owns the
 * setting is the one screen a person is least likely to be sitting on when the
 * sun goes down.
 *
 * The apply at the top re-asserts what the pre-paint script has already written,
 * from the same stored values, so it is idempotent by construction and costs two
 * attribute writes. It is kept because this function owns the attributes from
 * here on, and starting from a state it applied itself is cheaper to reason
 * about than starting from a state it is trusting the markup for. It is NOT
 * cross-tab synchronisation: nothing here listens for `storage`, so a choice
 * made in another tab reaches this one at its next load and not before.
 */
export function startAppearance(): () => void {
  applyAppearance(readAppearance());
  return watchSystemMode(() => applyAppearance(readAppearance()));
}
